// Brand Master — AdminCatalogue sub-tab.
// Provides list + add/edit modal + search + status toggle + delete-with-usage-guard.
// Also exports BrandQuickAdd for inline brand creation from the Product form.

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, MagnifyingGlass, X, Check,
  Warning, Tag,
} from "@phosphor-icons/react";

const inputCls = "rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] p-2.5 text-sm outline-none";

// Default export — "Brand Master" tab content
const BrandMasterTab = ({ user }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editor, setEditor] = useState(null); // { mode: 'create'|'edit', data }
  const [statusFilter, setStatusFilter] = useState(""); // "" | active | inactive

  const canEdit = ["admin", "manager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const r = await axios.get(`${API}/catalogue/brands`, { params });
      setBrands(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditor({
    mode: "create",
    data: { name: "", slug: "", description: "", logo_url: "", status: "active" },
  });
  const openEdit = (b) => setEditor({ mode: "edit", data: { ...b } });

  const toggleStatus = async (b) => {
    if (!canEdit) return;
    const action = b.status === "active" ? "deactivate" : "activate";
    try {
      await axios.patch(`${API}/catalogue/brands/${b.brand_id}/${action}?requester_role=${user?.role || ""}`);
      toast.success(`Brand ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const remove = async (b) => {
    if (!canEdit) return;
    if (!window.confirm(`Delete brand "${b.name}"? Brands used by products cannot be deleted.`)) return;
    try {
      await axios.delete(`${API}/catalogue/brands/${b.brand_id}?requester_role=${user?.role || ""}`);
      toast.success("Brand deleted");
      load();
    } catch (e) {
      // 409 = in use — show the server's exact message with a "Deactivate instead?" nudge
      const msg = e?.response?.data?.detail || "Delete failed";
      if (e?.response?.status === 409) {
        if (window.confirm(`${msg}\n\nDeactivate now?`)) {
          await toggleStatus(b);
        } else {
          toast.error(msg);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div data-testid="brand-master-root" className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="brand-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brand…"
              className="rounded-none border border-slate-300 bg-white p-2 pl-9 text-sm outline-none focus:border-[#002FA7] w-64"
            />
          </div>
          <select
            data-testid="brand-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-none border border-slate-300 bg-white p-2 text-sm outline-none focus:border-[#002FA7]"
          >
            <option value="">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>
        {canEdit && (
          <button
            data-testid="brand-add-btn"
            onClick={openNew}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-medium inline-flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> Add Brand
          </button>
        )}
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : brands.length === 0 ? (
          <div className="p-10 text-center text-slate-400 italic">
            <Tag size={24} className="mx-auto mb-2 text-slate-300" />
            No brands found. Click "Add Brand" to create your first brand.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="p-3 w-12">Logo</th>
                <th className="p-3">Name</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Status</th>
                <th className="p-3">Description</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.brand_id} data-testid={`brand-row-${b.brand_id}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name} className="w-10 h-10 object-contain border border-slate-200 bg-white" />
                    ) : (
                      <div className="w-10 h-10 border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">
                        <Tag size={14} />
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-medium">{b.name}</td>
                  <td className="p-3 text-slate-500 font-mono text-xs">{b.slug}</td>
                  <td className="p-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${
                      b.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                    }`}>{b.status}</span>
                  </td>
                  <td className="p-3 text-slate-600 max-w-md truncate">{b.description || "—"}</td>
                  <td className="p-3 text-right">
                    {canEdit && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`brand-toggle-${b.brand_id}`}
                          onClick={() => toggleStatus(b)}
                          className="p-2 hover:bg-slate-100 text-slate-600"
                          title={b.status === "active" ? "Deactivate" : "Activate"}
                        >
                          {b.status === "active" ? <ToggleRight size={16} weight="bold" className="text-emerald-600" /> : <ToggleLeft size={16} weight="bold" className="text-slate-400" />}
                        </button>
                        <button
                          data-testid={`brand-edit-${b.brand_id}`}
                          onClick={() => openEdit(b)}
                          className="p-2 hover:bg-slate-100 text-slate-700"
                          title="Edit"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          data-testid={`brand-delete-${b.brand_id}`}
                          onClick={() => remove(b)}
                          className="p-2 hover:bg-rose-50 text-rose-600"
                          title="Delete"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editor && (
        <BrandEditorModal
          editor={editor}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
          user={user}
        />
      )}
    </div>
  );
};

// --- Editor Modal (also used by BrandQuickAdd below) ---
export const BrandEditorModal = ({ editor, onClose, onSaved, user }) => {
  const [form, setForm] = useState(editor.data);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const upd = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        name: (form.name || "").trim(),
        slug: form.slug || "",
        description: form.description || "",
        logo_url: form.logo_url || "",
        status: form.status || "active",
      };
      if (!payload.name) {
        setErr("Brand name is required.");
        setSaving(false);
        return;
      }
      let saved;
      if (editor.mode === "create") {
        const r = await axios.post(`${API}/catalogue/brands?requester_role=${user?.role || ""}`, payload);
        saved = r.data;
        toast.success("Brand created");
      } else {
        const r = await axios.put(`${API}/catalogue/brands/${form.brand_id}?requester_role=${user?.role || ""}`, payload);
        saved = r.data;
        toast.success("Brand updated");
      }
      onSaved?.(saved);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Save failed";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="brand-editor-modal"
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white shadow-2xl max-w-xl w-full my-8"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
            {editor.mode === "create" ? "New Brand" : "Edit Brand"}
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {err && (
            <div data-testid="brand-form-error" className="flex items-start gap-2 border border-rose-300 bg-rose-50 text-rose-800 text-sm p-3">
              <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">Brand Name *</label>
            <input data-testid="brand-name-input" autoFocus value={form.name || ""} onChange={(e) => upd({ name: e.target.value })} className={inputCls} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">Slug / Code</label>
              <input data-testid="brand-slug-input" value={form.slug || ""} onChange={(e) => upd({ slug: e.target.value })} placeholder="auto-generated if empty" className={`${inputCls} font-mono text-xs`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">Status</label>
              <select data-testid="brand-status-input" value={form.status || "active"} onChange={(e) => upd({ status: e.target.value })} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">Logo URL</label>
            <input data-testid="brand-logo-input" value={form.logo_url || ""} onChange={(e) => upd({ logo_url: e.target.value })} placeholder="https://…" className={inputCls} />
            {form.logo_url && (
              <img src={form.logo_url} alt="preview" className="mt-2 w-16 h-16 object-contain border border-slate-200 bg-slate-50 p-1" onError={(e) => (e.target.style.display = "none")} />
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1.5">Description</label>
            <textarea data-testid="brand-description-input" value={form.description || ""} onChange={(e) => upd({ description: e.target.value })} rows={3} className={inputCls} />
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-none px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="brand-save-btn" type="submit" disabled={saving} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50">
            <Check size={14} weight="bold" /> {saving ? "Saving…" : editor.mode === "create" ? "Create Brand" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

// --- BrandQuickAdd: small component for inline use inside Product form ---
export const BrandQuickAdd = ({ user, onCreated }) => {
  const [open, setOpen] = useState(false);
  if (!["admin", "manager"].includes(user?.role)) return null;
  return (
    <>
      <button
        type="button"
        data-testid="brand-quickadd-btn"
        onClick={() => setOpen(true)}
        className="text-[11px] font-bold uppercase tracking-wider text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-1"
      >
        <Plus size={10} weight="bold" /> Add new brand
      </button>
      {open && (
        <BrandEditorModal
          editor={{ mode: "create", data: { name: "", slug: "", description: "", logo_url: "", status: "active" } }}
          onClose={() => setOpen(false)}
          onSaved={(saved) => { setOpen(false); onCreated?.(saved); }}
          user={user}
        />
      )}
    </>
  );
};

export default BrandMasterTab;
